
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useState, useEffect } from "react";
import { SubscriptionDialog } from "../SubscriptionDialog";
import { PlanUsageSection } from "./PlanUsageSection";
import { NotificationsSection } from "./NotificationsSection";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { getSubscriptionLimits } from "@/utils/subscriptionUtils";

type PlanFeatures = {
  company_resolutions: number;
  person_resolutions: number;
  csv_exports: boolean;
}

export const PlanUsageCard = () => {
  const { user } = useAuth();
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  
  console.group(`[PlanUsageCard] Subscription Status Debug`);
  console.log('User ID:', user?.id);

  const { data: subscriptionStatus, refetch: refetchSubscription } = useSubscriptionStatus(user?.id);
  
  console.log('Full Subscription Status:', JSON.stringify(subscriptionStatus, null, 2));
  console.log('Plan type:', subscriptionStatus?.plan);
  console.log('Is custom plan:', subscriptionStatus?.plan === 'custom');
  console.log('Metadata:', subscriptionStatus?.metadata);
  console.groupEnd();

  const [limits, setLimits] = useState({
    companiesLimit: 0,
    personResolutionsLimit: 0
  });

  useEffect(() => {
    const fetchLimits = async () => {
      if (subscriptionStatus) {
        try {
          console.log(`[PlanUsageCard] Fetching limits for plan ${subscriptionStatus.plan}`);
          console.log(`[PlanUsageCard] Is custom plan: ${subscriptionStatus.plan === 'custom'}`);
          console.log(`[PlanUsageCard] Custom resolutions from metadata:`, subscriptionStatus.metadata?.custom_resolutions);
          console.log(`[PlanUsageCard] Custom person resolutions from metadata:`, subscriptionStatus.metadata?.custom_person_resolutions);
          
          const result = await getSubscriptionLimits(subscriptionStatus);
          console.log(`[PlanUsageCard] Fetched limits for plan ${subscriptionStatus.plan}:`, result);
          setLimits({
            companiesLimit: result.companiesLimit,
            personResolutionsLimit: result.personResolutionsLimit
          });
        } catch (error) {
          console.error("Error fetching subscription limits:", error);
        }
      }
    };

    fetchLimits();
  }, [subscriptionStatus]);

  const { data: planFeatures, error: planError } = useQuery<PlanFeatures>({
    queryKey: ['plan-features', user?.id, subscriptionStatus?.plan, limits],
    queryFn: async () => {
      if (!subscriptionStatus) {
        console.log('[PlanUsageCard] No subscription status, returning default limits');
        return {
          company_resolutions: 0,
          person_resolutions: 0,
          csv_exports: true
        };
      }
      
      console.log(`[PlanUsageCard] Current plan: ${subscriptionStatus.plan}`);
      console.log(`[PlanUsageCard] Is custom plan: ${subscriptionStatus.plan === 'custom'}`);
      console.log(`[PlanUsageCard] Current limits:`, limits);
      
      // For custom plans, use the limits from the subscription metadata
      if (subscriptionStatus.plan === 'custom') {
        console.log('[PlanUsageCard] Using custom plan limits:', {
          company_resolutions: limits.companiesLimit,
          person_resolutions: limits.personResolutionsLimit
        });
        return {
          company_resolutions: limits.companiesLimit,
          person_resolutions: limits.personResolutionsLimit,
          csv_exports: true
        };
      }
      
      const isInTrialOnly = subscriptionStatus.plan === 'trial'
      if (isInTrialOnly) {
        console.log('[PlanUsageCard] User is in trial, returning zero limits');
        return {
          company_resolutions: 0,
          person_resolutions: 0,
          csv_exports: true
        };
      }
      
      console.log('[PlanUsageCard] Returning limits from plan configuration');
      return {
        company_resolutions: limits.companiesLimit,
        person_resolutions: limits.personResolutionsLimit,
        csv_exports: true
      };
    },
    enabled: !!user && !!limits
  });

  // Comprehensive logging for debugging
  console.log(`[PlanUsageCard] Plan Features:`, planFeatures);
  console.log(`[PlanUsageCard] Companies Used:`, subscriptionStatus?.company_resolutions_used);
  console.log(`[PlanUsageCard] Companies Limit:`, planFeatures?.company_resolutions);

  return (
    <>
      <div className="space-y-4">
        <PlanUsageSection
          companiesCount={subscriptionStatus?.company_resolutions_used || 0}
          companiesLimit={planFeatures?.company_resolutions || 0}
          personResolutionsUsed={subscriptionStatus?.person_resolutions_used || 0}
          personResolutionsLimit={planFeatures?.person_resolutions || 0}
          onUpgradeClick={() => {
            setShowSubscriptionDialog(true);
          }}
        />
        <NotificationsSection />
      </div>
      <SubscriptionDialog
        open={showSubscriptionDialog}
        onOpenChange={setShowSubscriptionDialog}
        userId={user?.id || ''}
        showPlansInitially={true}
      />
    </>
  );
};
